<template>
    <div class='d-flex'>
        <v-skeleton-loader v-if='isLoading' v-bind='attrs' width='600'
            type='list-item, list-item-three-line, card-heading'>
        </v-skeleton-loader>

        <div v-else style='width: 100%'>
            <div class='d-flex align-center justify-space-between mb-4'>
                <ButtonBack />
                <EventNavbar />
            </div>

            <div style="margin-bottom: 30px;">
                <div>
                    <v-btn 
                        :key="chipItem.name" v-for='chipItem in normalizedFeedFilterChips'
                        @click='getAndCallDynamicFunction(chipItem.content)' 
                        class='post__btn-filters'
                        :class="{ 'post__btn-filters-active': isButtonActive(chipItem.content) }"
                    >
                        {{ chipItem.name }}
                    </v-btn>
                </div>
            </div>

            <div v-if="!isLoading && !groupedByMonthAndDay.length" style='width: 100%'>
                <NewsFeedEmpty title='Session is empty at the moment.' description='Check back later.' />
            </div>

            <PostFilterModal 
                v-on:closeModal='closeModal' 
                :show='showModal' 
                :selected-button='selectedButton'
                :currentActiveButtonTitle='currentActiveButtonTitle' 
                :current-active-button='this.currentActiveButton'
                @applySelection='applySelection' 
                @setDate='setDateTimeRange' 
                @setItems='setSelectedItems' 
            />

            <div v-for='{ date, data } in groupedByMonthAndDay' :key='date'>
                <div class='agenda-page__title'>{{ date }}</div>

                <div v-for='(agenda, index) in data' :key='index'>
                    <router-link 
                        v-if='agenda.is_extended' 
                        :to="{
                            name: 'event-session-by-id', 
                            params: {
                                eventId: agenda.events_id,
                                sessionId: agenda.events_agenda_id
                            }
                        }" 
                        class='d-flex text-decoration-none' 
                        style='width: 100%;'
                    >
                        <AgendaCard :agenda-event='agenda' />
                    </router-link>
                    <AgendaCard v-else :agenda-event='agenda' />
                </div>
            </div>

            <div class='d-flex my-9 justify-center home-page__pagination' v-if='!isLoading && agendaEvent.length > 0'>
                <v-pagination 
                    color='#F2F2F2' 
                    :total-visible='10' 
                    circle 
                    v-model='filters.page'
                    @input='(e) => onPageChange(e)' 
                    :length='+this.pageCountAgendas'
                ></v-pagination>
            </div>
        </div>
    </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import AgendaCard from '@/components/Event/AgendaCard'
import PostFilterModal from '@/components/Modals/PostFilterModal'
import NewsFeedEmpty from '@/components/Post/NewsFeedEmpty'
import EventNavbar from '@/components/Event/EventNavbar.vue'
import ButtonBack from '@/components/ButtonBack'
import _ from 'lodash'

export default {
    name: 'AgendasPage',
    components: {
        AgendaCard,
        PostFilterModal,
        NewsFeedEmpty,
        EventNavbar,
        ButtonBack
    },
    methods: {
        ...mapActions('event', ['getAgendaEvent', 'getEventTracks', 'clearEventTracks']),

        onPageChange(page) {
            const params = {
                page: page,
                tracks: this.filters.tracks,
                to_timestamp: this.convertDateToTimeStamp(this.filters.date.to),
                from_timestamp: this.convertDateToTimeStamp(this.filters.date.from),
            }
            const queryString = this.formatFilters(params)
            this.getAgendas(queryString)
        },
        pad(number) {
            return (number < 10 ? '0' : '') + number;
        },

        formatFilters(filters) {
            const { page, to_timestamp, from_timestamp, tracks, filter } = filters

            const tracksIds = tracks.reduce((prev, next) => {
                return `${prev}&tracks_id[]=${next}`
            }, '')

            let query = `&event_id=${this.$route.params.id}${tracksIds}&page=${page}`
            if (to_timestamp) {
                query += `&to_timestamp=${to_timestamp}`
            }
            if (from_timestamp) {
                query += `&from_timestamp=${from_timestamp}`
            }
            if (this.isMyAgendaPage) {
                query += '&filter=scheduled'
            }
            if (filter) {
                query += `&filter=${filter}`
            }
            return query
        },

        async getAgendas(additionalQueryParams = '') {
            await this.getAgendaEvent(additionalQueryParams)
        },
        closeModal() {
            this.showModal = false

            const mutuallyExclusiveButtons = ['clear']

            this.selectedButton = this.getFilteredChips(
                this.selectedButton,
                this.currentActiveButton,
                mutuallyExclusiveButtons
            )
        },
        setSelectedItems(item) {
            const { listItem, selectedType, idColumnName = 'id' } = item

            this.setDynamicItems(listItem, selectedType, idColumnName)
        },
        setDynamicItems(item, selectedType, idColumnName) {
            if (this[selectedType].includes(item[idColumnName])) {
                this[selectedType] = this[selectedType].filter(
                    (itemFilter) => itemFilter !== item[idColumnName],
                )
            } else {
                this[selectedType] = [...this[selectedType], item[idColumnName]]
            }
        },
        applySelection() {
            if (this.currentActiveButton === 'date') {
                this.filters.date = this.selectedDateRange
            }
            this.filters.tracks = this.selectedTracks

            this.showModal = false
        },
        setDateTimeRange(item) {
            const { date, time, type } = item;

            if (!this.isDateFilterValid(this.selectedDateRange, item)) {
                this.showError = true;
                this.error = `The end date of the filter must be greater than the start date`
            }

            if (this.isDateFilterValid(this.selectedDateRange, item)) {
                this.showError = false;
                this.error = ''
            }

            const range = this.selectedDateRange ?? { from: null, to: null };

            this.selectedDateRange = { ...range, [type]: `${date} ${time}` }
        },
        convertDateToTimeStamp(date) {
            if (!date) {
                return null
            }

            return new Date(date).getTime() / 1000
        },
        isDateFilterValid(currentDateRange, dateValue) {
            const { date, time, type } = dateValue
            const { from, to } = currentDateRange || {}

            const isThisEndDateForFullRange = from && (type === 'to');
            const isThisStartDateForFullRange = to && (type === 'from');

            if (isThisEndDateForFullRange) {
                const fromTimestamp = new Date(currentDateRange.from).getTime();
                const toTimestamp = new Date(`${date} ${time}`).getTime()

                return toTimestamp > fromTimestamp
            }

            if (isThisStartDateForFullRange) {
                const fromTimestamp = new Date(`${date} ${time}`).getTime();
                const toTimestamp = new Date(currentDateRange.to).getTime();

                return fromTimestamp < toTimestamp
            }

            return true;
        },
        setActiveButtonTitle() {
            this.currentActiveButtonTitle = this.normalizedFeedFilterChips.find(
                item => item.content === this.currentActiveButton
            ).name
        },
        getAndCallDynamicFunction(name) {
            const dynamicFunctionName = `${name}ButtonHandler`
            const dynamicFunction = this[dynamicFunctionName]

            if (typeof dynamicFunction === 'function') {
                dynamicFunction()
            } else {
                console.error('Function not found')
            }
        },
        isButtonActive(name) {
            return this.selectedButton.includes(name)
        },
        filterSpecifiedValues(list, filterValues) {
            return list.filter(item => !filterValues.includes(item))
        },
        getFilteredChips(
            selectedButtons,
            handleButtonName,
            mutuallyExclusiveButtons
        ) {
            const isButtonPresentInlist = this.selectedButton.includes(handleButtonName)

            if (!isButtonPresentInlist) {
                return this.filterSpecifiedValues([...this.selectedButton, handleButtonName], mutuallyExclusiveButtons)
            } else {
                const filteredValues = this.filterSpecifiedValues(this.selectedButton, handleButtonName, [handleButtonName])

                if (filteredValues.length <= 0) {
                    return ['clear'];
                }

                return filteredValues;
            }
        },
        clearButtonHandler() {
            this.currentActiveButton = 'clear'
            this.currentActiveButtonTitle = ''
            this.selectedButton = ['clear']
            this.selectedTracks = []

            this.selectedDateRange = {
                from: null,
                to: null
            }

            this.filters = {
                filter: null,
                date: {
                    to: null,
                    from: this.resetFromTimeStamp,
                },
                tracks: []
            }
            this.showModal = false
        },
        registeredButtonHandler() {
            this.currentActiveButton = 'registered'

            this.currentActiveButtonTitle = this.normalizedFeedFilterChips.find(
                item => item.content === this.currentActiveButton
            ).name

            const mutuallyExclusiveButtons = ['clear']

            this.selectedButton = this.getFilteredChips(
                this.selectedButton,
                this.currentActiveButton,
                mutuallyExclusiveButtons
            )

            const isHandleButtonActive = this.selectedButton.includes(this.currentActiveButton)
            const params = {
                page: this.filters.page,
                tracks: this.filters.tracks,
                to_timestamp: this.convertDateToTimeStamp(this.filters.date.to),
                from_timestamp: this.convertDateToTimeStamp(this.filters.date.from)
            }
            if (isHandleButtonActive) {
                params.filter = 'registered'
            }
            const queryString = this.formatFilters(params)
            this.getAgendas(queryString)
        },
        dateButtonHandler() {
            this.currentActiveButton = 'date'

            this.currentActiveButtonTitle = this.normalizedFeedFilterChips.find(
                item => item.content === this.currentActiveButton
            ).name

            const mutuallyExclusiveButtons = ['clear', 'past']

            this.selectedButton = this.getFilteredChips(
                this.selectedButton,
                this.currentActiveButton,
                mutuallyExclusiveButtons
            )

            this.setActiveButtonTitle()

            const isHandleButtonActive = this.selectedButton.includes(this.currentActiveButton)
            const isPastButtonActive = this.selectedButton.includes('past')

            if (!isHandleButtonActive) {
                this.filters = {
                    ...this.filters,
                    date: isPastButtonActive ? 
                    {
                        from: null,
                        to: Math.floor(Date.now())
                    }
                    :
                    {
                        from: this.resetFromTimeStamp,
                        to: null
                    }
                }

                this.selectedDateRange = {
                    from: null,
                    to: null
                };
            }

            this.showModal = isHandleButtonActive
        },
        pastButtonHandler() {
            this.currentActiveButton = 'past'
            const mutuallyExclusiveButtons = ['clear', 'date']

            this.selectedButton = this.getFilteredChips(
                this.selectedButton,
                this.currentActiveButton,
                mutuallyExclusiveButtons
            )

            const isHandleButtonActive = this.selectedButton.includes(this.currentActiveButton)

            const initDateFilter = {
                from: isHandleButtonActive ? null : this.resetFromTimeStamp,
                to: isHandleButtonActive ? Math.floor(Date.now()) : null
            }

            this.filters = {
                ...this.filters,
                date: initDateFilter
            }

            this.selectedDateRange = initDateFilter
        },
        tracksButtonHandler() {
            this.currentActiveButton = 'tracks'

            this.currentActiveButtonTitle = this.normalizedFeedFilterChips.find(
                item => item.content === this.currentActiveButton
            ).name

            const mutuallyExclusiveButtons = ['clear']

            this.selectedButton = this.getFilteredChips(
                this.selectedButton,
                this.currentActiveButton,
                mutuallyExclusiveButtons
            )

            this.setActiveButtonTitle()

            const isHandleButtonActive = this.selectedButton.includes(this.currentActiveButton)

            if (!isHandleButtonActive) {
                this.selectedTracks = []
                this.filters = {
                    ...this.filters,
                    tracks: []
                }
            }

            this.showModal = isHandleButtonActive
        },
    },
    watch: {
        'filters.date': function (next, prev) {
            const isValuesEmptyOrEqual = (next.length <= 0 && prev.length <= 0) ||
                (_.isEqual(next, prev))

            if (isValuesEmptyOrEqual) {
                return false
            }

            const { from, to } = next;
            const params = {
                tracks: this.filters.tracks,
                to_timestamp: this.convertDateToTimeStamp(to),
                from_timestamp: this.convertDateToTimeStamp(from),
                page: 1,
            }
            if (this.selectedButton.includes('registered')) {
                params.filter = 'registered'
            }
            this.filters.page = 1
            const queryString = this.formatFilters(params)
            this.getAgendaEvent(queryString)
        },
        'filters.tracks': function (next, prev) {
            const isValuesEmptyOrEqual = (next.length <= 0 && prev.length <= 0) ||
                (_.isEqual(next, prev))

            if (isValuesEmptyOrEqual) {
                return false
            }

            const params = {
                tracks: this.filters.tracks,
                to_timestamp: this.convertDateToTimeStamp(this.filters.date.to),
                from_timestamp: this.convertDateToTimeStamp(this.filters.date.from),
                page: 1,
            }
            if (this.selectedButton.includes('registered')) {
                params.filter = 'registered'
            }
            this.filters.page = 1
            const queryString = this.formatFilters(params)
            this.getAgendaEvent(queryString)
        },
    },

    created() {
        if (this.$route.params.id !== undefined) {
            this.filters.date = {
                from: this.resetFromTimeStamp,
                to: null
            }
            this.getEventTracks({ eventId: this.$route.params.id, page: this.filters.page })
        }
    },
    beforeDestroy() {
        this.clearEventTracks()
    },

    computed: {
        ...mapGetters('event', ['isLoading', 'pageCountAgendas', 'agendaEvent', 'eventTracks']),

        groupedByMonthAndDay() {
            return Object.values(
                this.agendaEvent.reduce((prev, current) => {
                    const fromDate = new Date(current.events_agenda_from * 1000)
                    fromDate.setHours(fromDate.getHours())
                    const month = fromDate.toLocaleString('en-US', { month: 'long' })
                    const year = fromDate.getFullYear()
                    const day = fromDate.getDate()
                    const key = `${month} ${day}, ${year}`

                    if (!prev[key]) {
                        prev[key] = {
                            date: key,
                            data: [],
                        }
                    }

                    prev[key].data.push(current)

                    return prev
                }, {}),
            ).map((item) => {
                const { data } = item

                return {
                    ...item, data: data.sort((a, b) => {
                        const { events_agenda_from: aEventsFrom } = a
                        const { events_agenda_from: bEventsFrom } = b

                        return aEventsFrom - bEventsFrom
                    }),
                }
            })
        },
        normalizedFeedFilterChips() {
            const chips = [
                {
                    name: 'All',
                    content: 'clear'
                },
                {
                    name: 'Registered',
                    content: 'registered'
                },
                {
                    name: 'Date',
                    content: 'date'
                },
                {
                    name: 'Past sessions',
                    content: 'past'
                }
            ]
            if (this.eventTracks.length) {
                chips.push({
                    name: 'Tracks',
                    content: 'tracks'
                })
            }
            return chips
        },
        resetFromTimeStamp() {
            const currentDate = new Date()
            const year = currentDate.getFullYear()
            const month = ('0' + (currentDate.getMonth() + 1)).slice(-2)
            const day = ('0' + currentDate.getDate()).slice(-2)
            const hours = ('0' + currentDate.getHours()).slice(-2)
            const minutes = ('0' + currentDate.getMinutes()).slice(-2)

            return `${year}-${month}-${day} ${hours}:${minutes}`;
        },
        isMyAgendaPage() {
            return this.$route.name === 'my-agenda-events'
        }
    },

    data() {
        return {
            attrs: {
                elevation: 0,
            },
            selectedDateRange: {
                from: null,
                to: null
            },
            selectedTracks: [],
            filters: {
                page: 1,
                date: {
                    from: null,
                    to: null
                },
                tracks: []
            },
            showModal: false,
            selectedButton: ['clear'],
            currentActiveButton: '',
            currentActiveButtonTitle: '',
        }
    },
}
</script>

<style lang='scss'>
.agenda-page {
    background: #f1f1f1 !important;

    &__title {
        color: rgb(74, 74, 74);
        font-size: 18px;
        font-family: Montserrat;
        font-weight: bold;
        margin-bottom: 10px;
    }

    .post {
        &__search {
            background: white;
            height: 54px;
            border-radius: 10px;
            border: 1px solid rgb(230, 230, 230);
            max-width: 600px;
        }

        &__btn-filters {
            color: var(--chip-text-normal) !important;
            font-size: 14px;
            font-family: Montserrat, sans-serif;
            font-weight: bold;
            letter-spacing: 0.36px;
            margin-top: 20px;

            background-color: var(--chip-normal-background) !important;

            border-radius: 15px;
            box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.1);
            text-transform: capitalize !important;
            align-items: center;
            margin-right: 15px;

            &-active {
                background-color: var(--chip-selected-background) !important;
                color: var(--chip-text-selected) !important;
            }
        }

        @media screen and (max-width: 1264px) {
            &__search {
                max-width: none;
                width: 100%;
            }
        }
    }
}

@media screen and (min-width: 1200px) {
    .agenda-page-container {
        max-width: 100%;
        width: 100%;
    }
}

@media screen and (min-width: 1320px) {
    .agenda-page-container {
        max-width: 600px;
    }
}
</style>